import * as React from "react";
import { useParams, Link } from "react-router-dom";
import { Space, Input, Spin, Descriptions, message, Tree, Tag } from "antd";
import { PlusOutlined, CloudDownloadOutlined } from "@ant-design/icons";
import { ModelApiCommunicator } from "api/communicators/ModelApiCommunicator";
import { useAmplifyOrgSubscription } from "api";
import { formatFullDateFromTimeStamp } from "api/utils";
import { ListPageLayout } from "layouts";
import { TableListCellItems } from "components/Model/TableListCellItems";
import { convertDataToAntdTree } from "components/utils";
import { ScanGrid } from "components/ScanGrid";
import Axios from "axios";
import fileDownload from "js-file-download";

/**
 * PrintFileDetailPage Component
 * 
 * Displays comprehensive information about a specific print file (model) and its associated data.
 * Provides functionality for viewing, editing, and managing print file details including
 * file downloads, scan data, and result management.
 * 
 * Features:
 * - Complete print file information display
 * - File download capabilities
 * - Scan data visualization
 * - Print result management
 * - Tag management
 * - Real-time updates via Amplify subscriptions
 * - File tree visualization
 * - Status tracking and display
 * 
 * @returns {JSX.Element} The print file detail page component
 */
export function PrintFileDetailPage() {
  const { modelId } = useParams();
  return <PrintFileResultsFetchResults modelId={modelId} />;
}

const tagPlusStyle = {
  background: "#fff",
  borderStyle: "dashed",
};

const tagInputStyle = {
  width: 64,
  height: 22,
  marginInlineEnd: 8,
  verticalAlign: "top",
};

/**
 * PrintFileResultsFetchResults Component
 * 
 * Internal component that handles the main print file detail functionality including
 * data fetching, state management, and user interactions. This component manages
 * all the complex state and API interactions for the print file detail view.
 * 
 * @param {Object} props - Component props
 * @param {string} props.modelId - The ID of the print file (model) to display
 * @returns {JSX.Element} The print file results fetch results component
 */
function PrintFileResultsFetchResults({ modelId }) {
  const [modelApiCommunicator] = React.useState(new ModelApiCommunicator());
  const [addTagInputVisible, setAddTagInputVisible] = React.useState(false);
  const [addTagInputValue, setAddTagInputValue] = React.useState("");
  const addTagInputRef = React.useRef(null);
  const [loading, setLoading] = React.useState(false);
  /** @type {ReturnType<typeof React.useState<import('models').Model>} */
  const [model, setModel] = React.useState();
  const [messageApi, contextHolder] = message.useMessage();

  const fetchPageData = React.useCallback(async () => {
    if (modelId) {
      try {
        setLoading(true);
        const res = await modelApiCommunicator.fetch(modelId);
        setModel(res.data);
        console.log(res.data);
      } catch (err) {
        messageApi.error(err.message);
      }
      setLoading(false);
    }
  }, [modelId, messageApi, modelApiCommunicator]);

  useAmplifyOrgSubscription((update) => {
    if (update.type === "model" && !loading) {
      setModel((currModel) =>
        currModel.modelId === update.data.modelId ? update.data : currModel
      );
    }
  });

  React.useEffect(() => {
    fetchPageData();
  }, [fetchPageData]);

  const firstPrintResult = React.useMemo(() => {
    if (!model || model.printResults.length === 0) {
      return {};
    }
    return model.printResults[0];
  }, [model]);

  React.useEffect(() => {
    if (addTagInputVisible) {
      addTagInputRef.current?.focus();
    }
  }, [addTagInputRef, addTagInputVisible]);

  const handleTagInputChange = React.useCallback(
    async (e) => {
      setAddTagInputValue(e.target.value);
    },
    [setAddTagInputValue]
  );

  const downloadModelClick = React.useCallback(
    async () => {
      try {
        const res = await modelApiCommunicator.fetch(model.modelId);
        console.log(res.data);
        setLoading(true);
        const fileDownloadRes = await Axios.get(res.data.downloadUrl, {
          responseType: "blob",
        });
        setLoading(false);
        fileDownload(fileDownloadRes.data, res.data.gcPrintProjectName);
      } catch (err) {
        setLoading(false);
      }
    },
    [modelApiCommunicator, setLoading, model ]
  );

  const handleTagInputConfirm = React.useCallback(async () => {
    const modelTags = model?.tags || [];
    if (addTagInputValue && !modelTags.includes(addTagInputValue)) {
      modelTags.push(addTagInputValue);
      const updateRequest = { modelId: model.modelId, tags: modelTags };
      try {
        await modelApiCommunicator.update(updateRequest);
      } catch (err) {
        console.log(err);
        messageApi.error("Error updating tags " + err.message);
      }
    }
    setAddTagInputVisible(false);
    setAddTagInputValue("");
  }, [addTagInputValue, model, modelApiCommunicator, messageApi]);

  const showTagInput = React.useCallback(async () => {
    setAddTagInputVisible(true);
  }, [setAddTagInputVisible]);

  const removeTag = React.useCallback(
    async (removeTag) => {
      console.log("showTagInput Dialog");
      model.tags = model.tags.filter((tag) => tag !== removeTag);
      const updateRequest = { modelId: model.modelId, tags: model.tags };
      try {
        await modelApiCommunicator.update(updateRequest);
      } catch (err) {
        console.log(err);
        messageApi.error("Error updating tags " + err.message);
      }
    },
    [model, modelApiCommunicator, messageApi]
  );

  const modelDetailItems = React.useMemo(() => {
    if (!model) {
      return [];
    }
    return [
      {
        key: "model-name",
        label: "Model Name",
        children: (
          <>
            {model.modelName}
            <CloudDownloadOutlined 
            onClick={() => downloadModelClick()} 
            style={{ marginLeft: '10px' }}/>
          </>
        ),
      },
      {
        key: "print-project-name",
        label: "Print Project Name",
        children: model.gcPrintProjectName,
      },
      {
        key: "orig-upload-status",
        label: "Status",
        children: <TableListCellItems.StatusBadge status={model.status} />,
      },
      {
        key: "date-added",
        label: "Date Added",
        children: formatFullDateFromTimeStamp(model.dateAdded),
        span: 3,
      },
      {
        key: "part-count",
        label: "Part Count",
        children: (() => {
          return model.extractedFileCounts?.referenceCads !== undefined ? model.extractedFileCounts.referenceCads : "N/A";
        })(),
      },
      {
        key: "scan-count",
        label: "Scan Count",
        children: (() => {
          return model.extractedFileCounts?.scans !== undefined ? model.extractedFileCounts.scans : "N/A";
        })(),
      },
      {
        key: "info",
        label: "File Introspection",
        children: (() => {
          let color = "green"
          let message = "passed";
          if(model.extractedFileCounts.referenceCads === 0 && model.extractedFileCounts.scans === 0) {
            message =  "No parts or scans were detected in the file";
            color = "red";
          } else if(model.extractedFileCounts.referenceCads !== model.extractedFileCounts.scans) {
            message = "Mismatched number of parts and scans detected in the file";
            color = "red";
          }
          return <span style={{ color }}>{message}</span>;
        })(),
      },
      {
        key: "print-notes",
        label: "Notes",
        children: model.notes,
      },
      {
        key: "orig-printer",
        label: "Printer",
        children: model.manifestMetadata?.project?.gender?.name
          ? model.manifestMetadata?.project?.gender?.name
          : "NA",
      },
      {
        key: "orig-metadata",
        label: "Original Metadata",
        children: (
          <Tree
            treeData={convertDataToAntdTree(model.manifestMetadata)}
            height={233}
          />
        ),
      },
      {
        key: "model-tags",
        label: "Tags",
        children: (
          <>
            {model.tags && model.tags.length > 0 ? (
              model.tags.map((tag) => (
                <Tag
                  key={tag}
                  color="blue"
                  closable
                  onClose={() => removeTag(tag)}
                >
                  {tag}
                </Tag>
              ))
            ) : (
              <span>No tags</span>
            )}
            {addTagInputVisible ? (
              <Input
                ref={addTagInputRef}
                type="text"
                size="small"
                style={tagInputStyle}
                value={addTagInputValue}
                onChange={handleTagInputChange}
                onBlur={handleTagInputConfirm}
                onPressEnter={handleTagInputConfirm}
              />
            ) : (
              <Tag
                style={tagPlusStyle}
                icon={<PlusOutlined />}
                onClick={showTagInput}
              >
                New Tag
              </Tag>
            )}
          </>
        ),
      },
      {
        key: "datasets",
        label: "In Datasets",
        children: (
          <>
            {model.datasetIds && (model.datasets ?? []).length > 0 ? (
              model.datasets.map((dataset, index) => (
                <React.Fragment key={index}>
                  -{" "}
                  <Link to={`/datasets/${dataset.datasetId}`}>
                    {dataset.datasetName}
                  </Link>
                  <br />
                </React.Fragment>
              ))
            ) : (
              <span>none</span>
            )}
          </>
        ),
      },
    ];
  }, [model, addTagInputVisible, addTagInputValue, handleTagInputChange, handleTagInputConfirm, showTagInput, downloadModelClick, removeTag]);

  return (
    <ListPageLayout>
      {contextHolder}
      <Space
        direction="vertical"
        size="middle"
        style={{
          display: "flex",
        }}
      >
        {loading ? (
          <span>
            <Spin /> Loading...
          </span>
        ) : (
          <div>
            <Descriptions
              title={
                <div>
                  <h3 style={{ paddingLeft: "16px", display: "inline-block" }}>
                    Print File -
                  </h3>
                  <span> {model && model.modelName}</span>
                </div>
              }
              bordered
              items={modelDetailItems}
            />
            
            <div style={{ display: 'flex', gap: '16px', width: '100%' }}>
              <div>
                <h3>Tray</h3>
                <img src={model?.thumbnailUrl ?? "https://via.placeholder.com/400"} alt="Tray" />
              </div>
              <div>
                <h3>Parts</h3>
                <ScanGrid
                  scans={firstPrintResult.scanUploads}
                  loading={loading.active}
                />
              </div>
            </div>
          </div>
        )}
      </Space>
    </ListPageLayout>
  );
}
